import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import {
  ASSIGNMENT_TONE,
  interventionDay,
} from 'pages/AdminIssues/components/upcomingAssignment';
import {
  REQUEST_STATUSES,
  type FollowUpStats as FollowUpCounts,
  type InterventionRequestStatus,
} from 'api/interventionRequestsApi';
import { followUpStatusLabel, statusTone } from '../vocabulary';

/**
 * The five figures above the table — and each one is a **filter**: clicking a card narrows the
 * list to that status, clicking it again clears it. The first card is the way back to everything.
 *
 * Two lines per card since D48: requests on top, **missions** underneath. Several statuses now
 * coexist on one mission, so « 12 en attente » is not 12 missions, and one figure alone would let
 * a reader believe it is.
 *
 * The counts come from the API over the current filters **minus** the status one, so the
 * breakdown keeps describing the same population once a card is pressed.
 */

const CARD =
  'flex flex-col gap-[11px] rounded-[13px] border bg-card px-[19px] py-[18px] text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-colors hover:bg-accent/40';

const BADGE = 'inline-flex h-[21px] items-center rounded-[6px] px-2 text-[11.5px] font-semibold';

/** The period the figures actually cover — never the mock-up's fixed « 3 derniers jours ». */
const periodLabel = (t: TFunction, from?: string, to?: string): string => {
  const start = from ? interventionDay(from) : null;
  const end = to ? interventionDay(to) : null;
  if (start && end) return t('From {{from}} to {{to}}', { from: start, to: end });
  if (start) return t('Since {{from}}', { from: start });
  if (end) return t('Until {{to}}', { to: end });
  return t('All periods');
};

interface StatCardProps {
  title: string;
  badge: string;
  tone: string;
  requests: number;
  /** The mission line, already worded — `null` when the figure is not knowable (see below). */
  missions: { count: number; label: string } | null;
  /** Shown in the mission line's place: the period, when no mission figure can be claimed. */
  fallback: string;
  pressed: boolean;
  onClick: () => void;
}

const StatCard = ({
  title,
  badge,
  tone,
  requests,
  missions,
  fallback,
  pressed,
  onClick,
}: StatCardProps) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(CARD, pressed && 'border-foreground/40 ring-1 ring-foreground/30')}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-[13px] font-medium text-muted-foreground">{title}</span>
        <span className={cn(BADGE, tone)}>{badge}</span>
      </span>
      <span className="flex items-baseline gap-[7px]">
        <span className="text-[30px] font-semibold leading-none tracking-[-0.02em]">{requests}</span>
        <span className="text-[13px] text-muted-foreground">
          {t('request', { count: requests })}
        </span>
      </span>
      {missions ? (
        <span className="flex items-baseline gap-[6px] text-[12.5px] text-muted-foreground">
          <span className="font-semibold text-foreground">{missions.count}</span>
          {missions.label}
        </span>
      ) : (
        <span className="text-[12.5px] text-muted-foreground">{fallback}</span>
      )}
    </button>
  );
};

interface FollowUpStatsProps {
  counts: FollowUpCounts;
  /** The status filter as it stands. A card is pressed only when it is the whole filter. */
  selected: InterventionRequestStatus[];
  /** `null` clears the status filter — what the first card does. */
  onSelect: (status: InterventionRequestStatus | null) => void;
  /**
   * Missions in the whole population, when the table is showing it. `undefined` under a status
   * filter: the page total is then narrowed to that status while these cards are not, and the
   * two figures would contradict each other on the same card.
   */
  totalMissions?: number;
  sentFrom?: string;
  sentTo?: string;
}

export const FollowUpStats = ({
  counts,
  selected,
  onSelect,
  totalMissions,
  sentFrom,
  sentTo,
}: FollowUpStatsProps) => {
  const { t } = useTranslation();
  // All five statuses, `superseded` included: the card says how many left, not how many are live.
  const total = REQUEST_STATUSES.reduce((sum, status) => sum + (counts[status]?.requests ?? 0), 0);
  const period = periodLabel(t, sentFrom, sentTo);
  const isOnly = (status: InterventionRequestStatus): boolean =>
    selected.length === 1 && selected[0] === status;
  const toggle = (status: InterventionRequestStatus) => onSelect(isOnly(status) ? null : status);
  // Missions are **not** summable across statuses — one mission carries several — so each card
  // prints the server's own `COUNT(DISTINCT issue_key)` rather than anything derived here.
  const missionsOf = (status: InterventionRequestStatus, label: string) => ({
    count: counts[status]?.missions ?? 0,
    label,
  });

  return (
    <div className="mb-[26px] grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
      <StatCard
        title={t('Sent requests')}
        badge={t('Total')}
        tone={ASSIGNMENT_TONE.unassigned}
        requests={total}
        missions={
          totalMissions === undefined
            ? null
            : { count: totalMissions, label: t('mission', { count: totalMissions }) }
        }
        fallback={period}
        pressed={selected.length === 0}
        onClick={() => onSelect(null)}
      />
      <StatCard
        title={t('Accepted requests')}
        badge={followUpStatusLabel(t, 'accepted')}
        tone={statusTone('accepted')}
        requests={counts.accepted.requests}
        missions={missionsOf(
          'accepted',
          t('filled mission', { count: counts.accepted.missions })
        )}
        fallback={period}
        pressed={isOnly('accepted')}
        onClick={() => toggle('accepted')}
      />
      <StatCard
        title={t('Pending requests')}
        badge={followUpStatusLabel(t, 'pending')}
        tone={statusTone('pending')}
        requests={counts.pending.requests}
        missions={missionsOf(
          'pending',
          t('mission concerned', { count: counts.pending.missions })
        )}
        fallback={period}
        pressed={isOnly('pending')}
        onClick={() => toggle('pending')}
      />
      {/*
        « Refusées » alone, and no « expirées » beside it: D5 deleted the status, there is no
        expiry job, and a figure counting one would always read zero.
      */}
      <StatCard
        title={t('Refused requests')}
        badge={followUpStatusLabel(t, 'declined')}
        tone={statusTone('declined')}
        requests={counts.declined.requests}
        missions={missionsOf(
          'declined',
          t('mission concerned', { count: counts.declined.missions })
        )}
        fallback={period}
        pressed={isOnly('declined')}
        onClick={() => toggle('declined')}
      />
      {/*
        The fifth card, new with L5b: `superseded` is written now (D46), so without it the four
        figures no longer add up to the requests that were sent — and a reader would chase the gap.
      */}
      <StatCard
        title={t('Superseded requests')}
        badge={followUpStatusLabel(t, 'superseded')}
        tone={statusTone('superseded')}
        requests={counts.superseded.requests}
        missions={missionsOf(
          'superseded',
          t('mission taken by another technician', { count: counts.superseded.missions })
        )}
        fallback={period}
        pressed={isOnly('superseded')}
        onClick={() => toggle('superseded')}
      />
    </div>
  );
};
